import {
  useEffect,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type ImgHTMLAttributes,
  type JSX,
  type MouseEvent,
  type SyntheticEvent,
} from 'react';

import { requestBlob } from '../api/http';

export interface AuthenticatedImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  fileId: string;
}

export function AuthenticatedImage({
  fileId,
  onError,
  ...props
}: AuthenticatedImageProps): JSX.Element | null {
  const [src, setSrc] = useState<string | null>(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    void requestBlob(`/files/${fileId}`)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (active) onErrorRef.current?.({} as SyntheticEvent<HTMLImageElement>);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId]);
  return src ? <img {...props} src={src} onError={onError} /> : null;
}

export interface AuthenticatedFileLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'download'> {
  fileId: string;
  filename?: string;
  download?: boolean;
}

export function AuthenticatedFileLink({
  fileId,
  filename,
  download = false,
  onClick,
  children,
  ...props
}: AuthenticatedFileLinkProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const open = async (event: MouseEvent<HTMLAnchorElement>): Promise<void> => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const blob = await requestBlob(`/files/${fileId}`);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      if (download) anchor.download = filename ?? 'download';
      else {
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
      }
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } finally {
      setBusy(false);
    }
  };
  return (
    <a
      {...props}
      href="#"
      aria-busy={busy}
      onClick={(event) => void open(event)}
    >
      {children}
    </a>
  );
}
